import React, { forwardRef, useContext, useEffect, useMemo } from 'react';
import { capitalize, useApi } from '@repo-level/core-front';
import { i18nInstance } from 'i18n';
import { useTranslation } from 'react-i18next';
import { renderToString } from 'react-dom/server';

import { PrivacyPolicyApi, privacyPolicy } from 'api/cms/privacyPolicy';
import { ModalTemplate } from 'components/ModalTemplate';
import { ModalDefaultProps } from 'components/Modal';
import { Spinner } from 'components/Spinner';
import { ModalTemplateSettings } from 'components/ModalTemplate/types';
import { CommonUtilsContext } from 'contexts/commonUtils';
import { formatDateByLocale } from '../../../helpers/date';

import { ScStaticPage } from 'components/StaticModal/styled';
import { useIsAustralia } from 'hooks/useIsAustralia';
import { Locale } from 'languages';

export const generatePrivacyPolicyContent = ({
  text,
  tableHtml,
}: PrivacyPolicyApi) => {
  return text.replace(
    /\{\{table\}\}/g,
    tableHtml
      ? renderToString(
          <div
            className="horizontal-scroll"
            dangerouslySetInnerHTML={{
              __html: tableHtml,
            }}
          />
        )
      : ''
  );
};

export const PrivacyPolicyModal = forwardRef<HTMLDivElement, ModalDefaultProps>(
  ({ onClose }, ref) => {
    const {
      t,
      i18n: { language: locale },
    } = useTranslation('glossary');
    const { language } = i18nInstance;

    const { isMobileS } = useContext(CommonUtilsContext);
    const isAustralia = useIsAustralia();

    const [termsRequest, { pending, data }] = useApi(privacyPolicy);

    useEffect(() => {
      termsRequest(isAustralia ? Locale.ENGLISH_AUSTRALIA : language);
    }, [termsRequest, language, isAustralia]);

    const modalSettings: ModalTemplateSettings = useMemo(() => {
      return isMobileS
        ? { type: 'mobile', mode: { type: 'bottom' } }
        : {
            type: 'desktop',
            size: 'large',
            mode: { type: 'default' },
          };
    }, [isMobileS]);

    const formatDate = (date: string) => formatDateByLocale(date, locale);

    return (
      <>
        {pending ? (
          <Spinner size={64} absoluteCenter />
        ) : (
          data && (
            <ModalTemplate
              ref={ref}
              onClose={onClose}
              settings={modalSettings}
              title={data.titleH1}
            >
              <ScStaticPage>
                <div className="date">
                  <p>
                    {capitalize(t('effectiveFrom'))}{' '}
                    <span>{formatDate(data.effectiveFrom)}</span>
                  </p>
                  <p>
                    {capitalize(t('lastUpdated'))}{' '}
                    <span>{formatDate(data.lastUpdated)}</span>
                  </p>
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: generatePrivacyPolicyContent(data),
                  }}
                />
              </ScStaticPage>
            </ModalTemplate>
          )
        )}
      </>
    );
  }
);
